import { AccountMembersParams } from "./services/useAccountMembers";
import { GetApiIdentitiesParams } from "./services/useApiIdentities";
import { DetectorGroupsParams } from "./services/useDetectorGroups";
import { CreateExportTaskParams, GetExportResultParams } from "./services/useExports";
import { GetImageQueriesParams } from "./services/useImageQueries";
import { GetImageQueriesAnswersByTypeParams } from "./services/useImageQueriesAnswersByType";
import { GetImageQueriesExamplesParams } from "./services/useImageQueriesExamples";
import { GetImageQueriesFiltersByTypeParams } from "./services/useImageQueriesFiltersByType";
import { GetMeteringByAccountParams, GetMeteringByDetectorParams } from "./services/useMetering";
import { GetNotesPrams } from "./services/useNotes";

// Query keys for React Query cache management
export const API_CACHE_KEYS = {
  account: {
    limits: () => ["limits"] as const,
    billingInfo: () => ["billingInfo"] as const,
  },
  accountMembers: {
    all: ["account-members"] as const,
    lists: () => [...API_CACHE_KEYS.accountMembers.all, "list"] as const,
    list: (params?: AccountMembersParams) => [...API_CACHE_KEYS.accountMembers.lists(), params] as const,
  },
  apiIdentities: {
    all: ["api-identities"] as const,
    lists: () => [...API_CACHE_KEYS.apiIdentities.all, "list"] as const,
    list: (params?: GetApiIdentitiesParams) => [...API_CACHE_KEYS.apiIdentities.lists(), params] as const,
  },
  detector: {
    all: ["detectors"] as const,
    detail: (id: string) => [...API_CACHE_KEYS.detector.all, "detail", id] as const,
    pipelineDetails: (id: string) => [...API_CACHE_KEYS.detector.all, "pipeline-details", id] as const,
  },
  detectorGroups: {
    all: ["detector-groups"] as const,
    lists: () => [...API_CACHE_KEYS.detectorGroups.all, "list"] as const,
    list: (params?: DetectorGroupsParams) => [...API_CACHE_KEYS.detectorGroups.lists(), params] as const,
  },
  disagreement: {
    all: ["disagreements"] as const,
    list: (detectorId: string, state?: string) =>
      [...API_CACHE_KEYS.disagreement.all, "list", detectorId, state] as const,
    detail: (id: string) => [...API_CACHE_KEYS.disagreement.all, "detail", id] as const,
    count: (state?: string) => [...API_CACHE_KEYS.disagreement.all, "count", state] as const,
    check: (imageQueryId: string, answer: string) =>
      [...API_CACHE_KEYS.disagreement.all, "check", imageQueryId, answer] as const,
  },
  imageQueries: {
    all: ["image-queries"] as const,
    answers: () => [...API_CACHE_KEYS.imageQueries.all, "answers"] as const,
    answersByType: (params?: GetImageQueriesAnswersByTypeParams) =>
      [...API_CACHE_KEYS.imageQueries.answers(), params] as const,
    examples: (params?: GetImageQueriesExamplesParams) =>
      [...API_CACHE_KEYS.imageQueries.all, "examples", params] as const,
    filters: () => [...API_CACHE_KEYS.imageQueries.all, "filters"] as const,
    filtersByType: (params?: GetImageQueriesFiltersByTypeParams) =>
      [...API_CACHE_KEYS.imageQueries.filters(), params] as const,
    lists: () => [...API_CACHE_KEYS.imageQueries.all, "list"] as const,
    list: (params?: GetImageQueriesParams) => [...API_CACHE_KEYS.imageQueries.lists(), params] as const,
    detail: (id: string) => [...API_CACHE_KEYS.imageQueries.all, "detail", id] as const,
  },
  reviewPage: {
    summary: () => ["review-page-summary"] as const,
    pool: () => ["review-pool"] as const,
  },
  user: {
    all: ["users"] as const,
    me: () => ["me"] as const,
    profile: () => ["profile"] as const,
  },
  notes: {
    all: ["notes"] as const,
    lists: () => [...API_CACHE_KEYS.notes.all, "list"] as const,
    list: (params?: GetNotesPrams) => [...API_CACHE_KEYS.notes.lists(), params] as const,
  },
  labels: {
    all: ["labels"] as const,
    list: (imageQueryId?: string) => [...API_CACHE_KEYS.labels.all, "list", imageQueryId] as const,
  },
  onboardings: {
    all: ["onboardings"] as const,
  },
  activityStatus: {
    all: ["activity-status"] as const,
    get: () => [...API_CACHE_KEYS.activityStatus.all, "get"] as const,
  },
  inspections: {
    all: ["inspections"] as const,
    lists: () => [...API_CACHE_KEYS.inspections.all, "list"] as const,
    list: (url?: string) => [...API_CACHE_KEYS.inspections.lists(), url] as const,
    detail: (id: string) => [...API_CACHE_KEYS.inspections.all, "detail", id] as const,
  },
  labelerStatus: {
    all: ["labeler-status"] as const,
  },
  exports: {
    all: ["exports"] as const,
    create: (params?: CreateExportTaskParams) => [...API_CACHE_KEYS.exports.all, "create", params] as const,
    result: (params?: GetExportResultParams) => [...API_CACHE_KEYS.exports.all, "result", params] as const,
  },
  metering: {
    all: ["metering"] as const,
    byAccount: (params?: GetMeteringByAccountParams) => [...API_CACHE_KEYS.metering.all, "by-account", params] as const,
    byDetector: (params?: GetMeteringByDetectorParams) =>
      [...API_CACHE_KEYS.metering.all, "by-detector", params] as const,
  },
};
